import { ChevronDown } from "lucide-react";
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

import Button from "../button/Button";
import Checkbox from "../checkbox/Checkbox";
import { XsmallText } from "../typography/Typography";


const matchRatingFilterOptions = [
  { value: 5, label: "5" },
  { value: 4, label: "4" },
  { value: 3, label: "3" },
  { value: 2, label: "2" },
  { value: 1, label: "1" },
  { value: "No Rating", label: "No Rating" },
];

/**
 * MatchRatingFilter Component
 * 
 * @param {Object} props
 * @param {Set} props.value - Set of selected ratings (e.g., Set([1, 2, 3, 4, 5, "No Rating"]))
 * @param {Function} props.onChange - Callback function called when selection changes, receives new Set
 * @param {boolean} props.disabled - Whether the filter is disabled
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.placement - Placement of the dropdown menu (e.g., "right-0", "left-0")
 */
const MatchRatingFilter = ({
  value,
  onChange,
  disabled = false,
  className = "",
  placement = "right-0",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Handler for toggling match rating selection
  const handleToggleMatchRating = useCallback(
    (rating) => {
      if (disabled) return;
      const newSet = new Set(value);
      if (newSet.has(rating)) {
        newSet.delete(rating);
      } else {
        newSet.add(rating);
      }
      onChange(newSet);
    },
    [value, onChange, disabled]
  );

  // Get display text for match rating filter button
  const getFilterText = useCallback(() => {
    if (value.size === matchRatingFilterOptions.length) {
      return "All";
    }
    if (value.size === 0) {
      return "None";
    }
    // Separate numeric ratings and No Rating
    const numericRatings = Array.from(value)
      .filter((r) => typeof r === "number")
      .sort((a, b) => b - a);
    const hasNoRating = value.has("No Rating");

    const parts = [];
    if (numericRatings.length > 0) {
      parts.push(numericRatings.join(", "));
    }
    if (hasNoRating) {
      parts.push("No Rating");
    }
    return parts.join(", ");
  }, [value]);

  // Calculate dropdown position based on button position
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 192; // w-48 = 12rem = 192px
      const spacing = 8; // mt-2 = 0.5rem = 8px
      
      let left = buttonRect.left;
      let top = buttonRect.bottom + spacing;
      
      // Handle placement prop
      if (placement === "right-0" || placement.includes("right")) {
        left = buttonRect.right - dropdownWidth;
      } else if (placement === "left-0" || placement.includes("left")) {
        left = buttonRect.left;
      }
      
      // Ensure dropdown doesn't go off screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 8; // 8px padding from edge
      }
      if (left < 0) {
        left = 8;
      }
      
      // If dropdown would go below viewport, show it above the button
      const dropdownHeight = 200; // Approximate height
      if (top + dropdownHeight > viewportHeight) {
        top = buttonRect.top - dropdownHeight - spacing;
      }
      
      setDropdownPosition({ top, left });
    }
  }, [placement]);

  // Update position when dropdown opens or window resizes/scrolls
  // Use useLayoutEffect to update position before browser paint, preventing flicker
  useLayoutEffect(() => {
    if (dropdownOpen) {
      updateDropdownPosition();
      
      const handleResize = () => updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();
      
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true); // Use capture to catch all scroll events
      
      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [dropdownOpen, updateDropdownPosition]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      // Use a small delay to avoid closing immediately when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [dropdownOpen]);

  return (
    <>
      <div className={className.trim()} ref={dropdownRef}>
        <div ref={buttonRef} className="inline-block">
          <Button
            variant="tertiary"
            size="sm"
            rightIcon={<ChevronDown className="h-3 w-3" />}
            onClick={() => {
              if (!dropdownOpen) {
                // Calculate position synchronously BEFORE opening dropdown
                // This prevents the dropdown from flashing at (0, 0)
                updateDropdownPosition();
              }
              setDropdownOpen(!dropdownOpen);
            }}
            disabled={disabled}
          >
            {`Match Rating: ${getFilterText()}`}
          </Button>
        </div>
      </div>
      {dropdownOpen &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <div className="p-2">
              {matchRatingFilterOptions.map((option) => (
                <div
                  key={option.value}
                  className="px-3 py-2 hover:bg-gray-100 rounded cursor-pointer flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleMatchRating(option.value);
                  }}
                >
                  <Checkbox checked={value.has(option.value)} readOnly />
                  <XsmallText weight="medium">{option.label}</XsmallText>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MatchRatingFilter;

